// Builds a 1541 disk image (D64, 35 tracks, 174848 bytes) from a list of files. Only what a replay disk needs:
// a few files, no error info, the BAM and the directory in the usual place (track 18).

const SECTORS = (track) => (track <= 17 ? 21 : track <= 24 ? 19 : track <= 30 ? 18 : 17);
const TRACKS = 35;
const DIR_TRACK = 18;
const BLOCK = 256;
export const D64_SIZE = 174848;

const offset = (track, sector) => {
  let blocks = 0;
  for (let t = 1; t < track; t++) blocks += SECTORS(t);
  return (blocks + sector) * BLOCK;
};

// Padded PETSCII-ish name: upper case ASCII is the same code in PETSCII, the rest is $A0.
function putName(img, at, name, length) {
  for (let i = 0; i < length; i++) img[at + i] = i < name.length ? name.charCodeAt(i) & 0xff : 0xa0;
}

const TYPES = { SEQ: 0x81, PRG: 0x82 };

// files: [{ name: 'REPLAY', type: 'PRG' | 'SEQ', data: Uint8Array }], at most 8, names up to 16 characters.
export function buildD64({ diskName = 'PRINTY', diskId = '01', files }) {
  if (files.length > 8) throw new Error('at most 8 files');
  const img = new Uint8Array(D64_SIZE);
  const free = []; // free[track][sector]
  for (let t = 1; t <= TRACKS; t++) free[t] = Array.from({ length: SECTORS(t) }, () => true);
  free[DIR_TRACK][0] = false; // BAM
  free[DIR_TRACK][1] = false; // first directory sector

  // Sectors in the order a file uses them: from track 1 up (not 18), with an interleave of 10 inside a track.
  const interleaved = (n) => {
    const taken = new Array(n).fill(false);
    const order = [];
    let s = 0;
    for (let i = 0; i < n; i++) {
      while (taken[s]) s = (s + 1) % n;
      taken[s] = true;
      order.push(s);
      s = (s + 10) % n;
    }
    return order;
  };
  const allocate = (count) => {
    const got = [];
    for (let t = 1; t <= TRACKS && got.length < count; t++) {
      if (t === DIR_TRACK) continue;
      for (const s of interleaved(SECTORS(t))) {
        if (got.length === count) break;
        if (free[t][s]) {
          free[t][s] = false;
          got.push([t, s]);
        }
      }
    }
    if (got.length < count) throw new Error('the files do not fit on the disk');
    return got;
  };

  const dir = offset(DIR_TRACK, 1);
  img[dir] = 0;
  img[dir + 1] = 0xff; // last (only) directory sector
  files.forEach((f, i) => {
    const blocks = Math.max(1, Math.ceil(f.data.length / 254));
    const chain = allocate(blocks);
    chain.forEach(([t, s], k) => {
      const at = offset(t, s);
      const part = f.data.subarray(k * 254, (k + 1) * 254);
      if (k + 1 < chain.length) {
        img[at] = chain[k + 1][0];
        img[at + 1] = chain[k + 1][1];
      } else {
        img[at] = 0;
        img[at + 1] = part.length + 1; // index of the last used byte
      }
      img.set(part, at + 2);
    });
    const e = dir + i * 32;
    img[e + 2] = TYPES[f.type];
    img[e + 3] = chain[0][0];
    img[e + 4] = chain[0][1];
    putName(img, e + 5, f.name, 16);
    img[e + 30] = blocks & 0xff;
    img[e + 31] = blocks >> 8;
  });

  // BAM
  const bam = offset(DIR_TRACK, 0);
  img[bam] = DIR_TRACK;
  img[bam + 1] = 1;
  img[bam + 2] = 0x41; // DOS version "A"
  for (let t = 1; t <= TRACKS; t++) {
    const at = bam + 4 * t;
    let freeCount = 0;
    for (let s = 0; s < SECTORS(t); s++) {
      if (free[t][s]) {
        freeCount++;
        img[at + 1 + (s >> 3)] |= 1 << (s & 7);
      }
    }
    img[at] = freeCount;
  }
  putName(img, bam + 0x90, diskName, 16);
  img[bam + 0xa0] = 0xa0;
  img[bam + 0xa1] = 0xa0;
  img[bam + 0xa2] = diskId.charCodeAt(0);
  img[bam + 0xa3] = diskId.charCodeAt(1);
  img[bam + 0xa4] = 0xa0;
  img[bam + 0xa5] = 0x32; // "2A"
  img[bam + 0xa6] = 0x41;
  for (let i = 0xa7; i <= 0xaa; i++) img[bam + i] = 0xa0;
  return img;
}
